import { ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationWizardResource, NotificationWizardSteps } from '../notification-wizard-type';
import { MatTable } from '@angular/material/table';
import { UserService } from '../../../api-users/api/user.service';
import { NewsResource } from '../../../api-users/model/newsResource';
import { NewsService } from '../../../news/news.service';
import { ContactResource, ContactService } from 'sting';
import { OrderService } from '../../../api/api/order.service';
import { OrderResource } from '../../../api/model/orderResource';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-notification-wizard-text',
    templateUrl: './notification-wizard-text.component.html',
    styleUrls: ['./notification-wizard-text.styles.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NotificationWizardTextComponent implements OnInit, OnDestroy {
    @Output() saveEmitter: EventEmitter<string> = new EventEmitter<string>();
    @ViewChild('fileInput') fileInput!: ElementRef;
    @ViewChild(MatTable) table?: MatTable<any>;

    contacts?: ContactResource[];
    attachment?: string;

    news: NewsResource[] = [];
    newsCurrentPage = 1;
    newsPageSize = 10;
    newsTotalCount = 0;
    newsLoading = false;
    newsHasMore = true;
    newsSearchTerm = '';
    private _newsSearchSubject = new Subject<string>();
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    orders?: OrderResource[];
    order?: OrderResource;

    notificationWizard: NotificationWizardResource;
    validationErrors = {
        subject: false,
        androidMessage: false,
        iosMessage: false
    };
    attachmentTypes = [
        { name: 'Kontakt', type: 'contact' },
        { name: 'Zakázka', type: 'order' },
        { name: 'Novinka', type: 'news' },
        { name: 'Odkaz', type: 'link' },
        { name: 'Textová', type: 'rich_content' },
    ];
    editorConfig = {
        editable: true,
        spellcheck: true,
        height: '400px',
        minHeight: '100px',
        placeholder: 'Text... ',
        translate: 'true',
        imageResizeSensitivity: 2,
        defaultParagraphSeparator: 'p',
        defaultFontSize: '2',
        toolbarHiddenButtons: [['insertVideo', 'removeFormat', 'toggleEditorMode', 'fontSize', 'textColor', 'backgroundColor', 'fontName']],
    };

    constructor(
        public _changeDetectorRef: ChangeDetectorRef,
        public _userService: UserService,
        public _newsService: NewsService,
        private _contactService: ContactService,
        private _orderService: OrderService,
        private translateService: TranslateService,
    ) {
        if (sessionStorage.getItem('notificationWizard')) {
            // @ts-ignore
            this.notificationWizard = JSON.parse(sessionStorage.getItem('notificationWizard')) as NotificationWizard;
        } else {
            this.notificationWizard = {};
        }
    }

    async ngOnInit(): Promise<void> {
        this._changeDetectorRef.markForCheck();

        this._newsSearchSubject.pipe(
            debounceTime(300),
            takeUntil(this._unsubscribeAll)
        ).subscribe(searchTerm => {
            const newSearchTerm = (searchTerm || '').trim();
            const currentSearchTerm = (this.newsSearchTerm || '').trim();
            
            if (currentSearchTerm !== newSearchTerm) {
                this.newsSearchTerm = newSearchTerm;
                this.newsCurrentPage = 1;
                this.news = [];
                this.newsHasMore = true;
                this.loadMoreNews();
            }
        });

        // Restore attachment data when returning to this step
        if (this.notificationWizard.attachmentType?.type === 'rich_content' && this.notificationWizard.richContent) {
            this.attachment = this.notificationWizard.richContent;
        } else if (this.notificationWizard.attachment) {
            this.attachment = this.notificationWizard.attachment;
        }

        if (this.notificationWizard.attachmentType?.type === 'contact') {
            this.getContacts();
        }
        if (this.notificationWizard.attachmentType?.type === 'news') {
            this.getNews();
        }
        if (this.notificationWizard.attachmentType?.type === 'order') {
            this.getOrders();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    save() {
        this.validateForm();

        if (this.isFormValid()) {
            this.notificationWizard.attachment = this.attachment;
            this.notificationWizard.step = NotificationWizardSteps.Summaray;
            sessionStorage.setItem('notificationWizard', JSON.stringify(this.notificationWizard));
            this.saveEmitter.emit('');
        } else {
            this._changeDetectorRef.markForCheck();
        }
    }

    getContacts() {
        this._contactService.contactList().subscribe((value: any) => {
            this.contacts = value?.data;
            this._changeDetectorRef.markForCheck();
        });
    }

    getNews() {
        this.newsSearchTerm = '';
        this.newsCurrentPage = 1;
        this.news = [];
        this.newsHasMore = true;
        this.loadMoreNews();
    }

    onNewsSearch(event: { term: string } | string) {
        const term = typeof event === 'string' ? event : (event?.term || '');
        const trimmedTerm = term ? term.trim() : '';
        
        if (!trimmedTerm && this.newsSearchTerm) {
            this.newsSearchTerm = '';
            this.newsCurrentPage = 1;
            this.news = [];
            this.newsHasMore = true;
            this.loadMoreNews();
        } else {
            this._newsSearchSubject.next(trimmedTerm);
        }
    }

    loadMoreNews() {
        if (this.newsLoading || !this.newsHasMore) {
            return;
        }

        this.newsLoading = true;

        const filterObject: Record<string, unknown> = {};
        filterObject['filter[status]'] = 'published';
        if (this.newsSearchTerm && this.newsSearchTerm.trim()) {
            filterObject['filter[fulltext]'] = this.newsSearchTerm.trim();
        }
        const filter = Object.keys(filterObject).length > 0 ? filterObject : undefined;

        this._newsService.getListNewsForAdmin(this.newsCurrentPage, this.newsPageSize, undefined, undefined, undefined, undefined, filter).subscribe((value: any) => {
            const newsData = value?.data || [];
            const newItems = Array.isArray(newsData) ? newsData : [];

            if (this.newsCurrentPage === 1) {
                this.news = newItems;
            } else {
                this.news = [...(this.news || []), ...newItems];
            }

            if (value?.meta?.total !== undefined) {
                this.newsTotalCount = value.meta.total;
                this.newsHasMore = this.news.length < this.newsTotalCount;
            } else {
                this.newsHasMore = newItems.length === this.newsPageSize;
            }

            this.newsCurrentPage++;
            this.newsLoading = false;
            this._changeDetectorRef.markForCheck();
        }, () => {
            this.newsLoading = false;
            this._changeDetectorRef.markForCheck();
        });
    }

    onNewsScrollToEnd() {
        this.loadMoreNews();
    }

    getOrders() {
        this._orderService.orderList().subscribe((value: any) => {
            this.orders = value?.data || value;
            this._changeDetectorRef.markForCheck();
        });
    }

    setType(type: any) {
        if (this.notificationWizard.attachmentType === type) {
            this.notificationWizard.attachmentType = undefined;
            this.attachment = undefined;
            this.notificationWizard.richContent = undefined;
            this.notificationWizard.link = undefined;
            this.news = [];
            this.newsCurrentPage = 1;
            this.newsHasMore = true;
            this.newsSearchTerm = '';
        } else {
            this.attachment = undefined;
            this.notificationWizard.attachmentType = type;
            // Don't clear richContent and link when switching types to preserve them

            sessionStorage.setItem('notificationWizard', JSON.stringify(this.notificationWizard));

            if (type?.type === 'contact') {
                this.getContacts();
            }
            if (type?.type === 'news') {
                this.getNews();
            }
            if (type?.type === 'order') {
                this.getOrders();
            }
        }
    }

    selectOrder(order: OrderResource) {
        this.order = order;
        this.attachment = order.orderId.toString();
        sessionStorage.setItem('notificationWizard', JSON.stringify(this.notificationWizard));
    }

    onRichTextChange(value: string): void {
        this.notificationWizard.richContent = value;
    }

    trackUsersByFn(term: string, item: any) {
        return (
            (item.client && item.client.toLowerCase().includes(term.toLowerCase())) ||
            (item.phone && item.phone.toLowerCase().includes(term.toLowerCase())) ||
            (item.email && item.email.toLowerCase().includes(term.toLowerCase()))
        );
    }

    validateForm(): void {
        this.validationErrors.subject = !this.notificationWizard.subject || this.notificationWizard.subject.trim().length === 0;
        this.validationErrors.androidMessage = !this.notificationWizard.androidMessage || this.notificationWizard.androidMessage.trim().length === 0;
        this.validationErrors.iosMessage = !this.notificationWizard.iosMessage || this.notificationWizard.iosMessage.trim().length === 0;
    }

    clearFieldError(field: 'subject' | 'androidMessage' | 'iosMessage'): void {
        this.validationErrors[field] = false;
    }

    isFormValid(): boolean {
        const hasSubject = this.notificationWizard.subject && this.notificationWizard.subject.trim().length > 0;
        const hasAndroidMessage = this.notificationWizard.androidMessage && this.notificationWizard.androidMessage.trim().length > 0;
        const hasIosMessage = this.notificationWizard.iosMessage && this.notificationWizard.iosMessage.trim().length > 0;

        const androidMessageValid = !this.notificationWizard.androidMessage || this.notificationWizard.androidMessage.length <= 240;
        const iosMessageValid = !this.notificationWizard.iosMessage || this.notificationWizard.iosMessage.length <= 150;

        return hasSubject && hasAndroidMessage && hasIosMessage && androidMessageValid && iosMessageValid;
    }
}
